import React from 'react';
import { Plus, X } from 'lucide-react';

/**
 * Editable tier table for a single custom-menu phase.
 * Each tier = { label, quantity, price_per_person }.
 * Tier subtotal = quantity × price_per_person.
 * Phase subtotal = Σ tier subtotals.
 */
export const CustomMenuTiers = ({ tiers = [], onChange, testIdPrefix = 'tier' }) => {
  const update = (idx, patch) => {
    const next = tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t));
    onChange(next);
  };
  const add = () => onChange([...(tiers || []), { label: '', quantity: '', price_per_person: '' }]);
  const remove = (idx) => onChange(tiers.filter((_, i) => i !== idx));

  const phaseTotal = (tiers || []).reduce((s, t) => {
    const q = parseInt(t.quantity) || 0;
    const p = parseFloat(t.price_per_person) || 0;
    return s + q * p;
  }, 0);

  return (
    <div className="mt-2 border-l-2 border-[#c9a227] pl-3" data-testid={`${testIdPrefix}-wrap`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-[#1e3a5f]">Priced rows (e.g. 45 Chicken @ $45)</span>
        <button
          type="button"
          onClick={add}
          className="text-xs text-[#1e3a5f] hover:underline flex items-center gap-1"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus size={12} />Add Tier
        </button>
      </div>
      {(tiers || []).length === 0 ? (
        <p className="text-xs text-gray-400 italic">No tiers. Falls back to flat per-person price above.</p>
      ) : (
        <div className="space-y-1">
          {tiers.map((t, idx) => {
            const q = parseInt(t.quantity) || 0;
            const p = parseFloat(t.price_per_person) || 0;
            const sub = q * p;
            return (
              <div key={idx} className="grid grid-cols-12 gap-1 items-center" data-testid={`${testIdPrefix}-row-${idx}`}>
                <input
                  type="text"
                  placeholder="Label (e.g. Chicken)"
                  value={t.label || ''}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  className="col-span-5 px-2 py-1 text-xs border rounded"
                  data-testid={`${testIdPrefix}-label-${idx}`}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Qty"
                  value={t.quantity ?? ''}
                  onChange={(e) => update(idx, { quantity: e.target.value })}
                  className="col-span-2 px-2 py-1 text-xs border rounded text-right"
                  data-testid={`${testIdPrefix}-qty-${idx}`}
                />
                <div className="col-span-3 flex items-center gap-1">
                  <span className="text-xs text-gray-500">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={t.price_per_person ?? ''}
                    onChange={(e) => update(idx, { price_per_person: e.target.value })}
                    className="flex-1 px-1 py-1 text-xs border rounded text-right"
                    data-testid={`${testIdPrefix}-price-${idx}`}
                  />
                </div>
                <span
                  className="col-span-1 text-xs text-[#1e3a5f] font-semibold text-right"
                  data-testid={`${testIdPrefix}-subtotal-${idx}`}
                >
                  ${sub.toFixed(0)}
                </span>
                <button
                  type="button"
                  onClick={() => remove(idx)}
                  className="col-span-1 text-red-500 hover:text-red-700 flex justify-center"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                  title="Remove tier"
                >
                  <X size={14} />
                </button>
              </div>
            );
          })}
          {phaseTotal > 0 && (
            <div className="text-right text-xs font-bold text-[#1e3a5f] pr-7 pt-1 border-t border-dashed">
              Phase subtotal: ${phaseTotal.toFixed(2)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomMenuTiers;
